// compute-cell-fill.ts
/**
 * One write produced by a multi-cell paste: which (visible-row index, column
 * index) to write and the raw string value taken from the clipboard grid.
 */
export type CellFillWrite = {
  rowIndex: number;
  colIndex: number;
  value: string;
};

export type ComputeCellFillParams = {
  /** Clipboard contents, row-major (`grid[rowOffset][colOffset]`). */
  grid: string[][];
  /** Top-left target row index in visible-rows space. */
  anchorRowIndex: number;
  /** Top-left target column index in `columnOrder` space. */
  anchorColIndex: number;
  /**
   * Target rectangle height. 1 for a single-cell selection (the block then
   * expands to the clipboard height); otherwise the selection's row span.
   */
  targetRows: number;
  /** Target rectangle width (see {@link ComputeCellFillParams.targetRows}). */
  targetCols: number;
  /** Total visible rows — writes past this are clipped (never create rows). */
  maxRows: number;
  /** Total columns — writes past this are clipped. */
  maxCols: number;
};

/**
 * Maps a clipboard grid onto a target rectangle using Google-Sheets fill
 * semantics: each target cell at offset (r, c) takes `grid[r % rows][c % cols]`.
 *
 * - A single-cell target (targetRows/targetCols === 1) expands to the full grid
 *   size from the anchor (modulo is the identity since r < rows / c < cols).
 * - A multi-cell target keeps its own size; a smaller clipboard tiles, a larger
 *   one is clipped to the top-left subset.
 * - Everything is clipped to [maxRows, maxCols] so paste never overflows the
 *   table.
 */
export function computeCellFillPlan({
  grid,
  anchorRowIndex,
  anchorColIndex,
  targetRows,
  targetCols,
  maxRows,
  maxCols,
}: ComputeCellFillParams): CellFillWrite[] {
  const gridRows = grid.length;
  const gridCols = gridRows > 0 ? grid[0].length : 0;
  if (gridRows === 0 || gridCols === 0) return [];
  if (anchorRowIndex < 0 || anchorColIndex < 0) return [];

  // A single-cell target (1×1) is the anchor-expand case: grow to the clipboard
  // size. Any larger explicit selection keeps its own dimensions (clip or tile).
  const isSingleCell = targetRows <= 1 && targetCols <= 1;
  const spanRows = isSingleCell ? gridRows : targetRows;
  const spanCols = isSingleCell ? gridCols : targetCols;

  const writes: CellFillWrite[] = [];
  for (let r = 0; r < spanRows; r++) {
    const rowIndex = anchorRowIndex + r;
    if (rowIndex >= maxRows) break; // clip to table bottom
    for (let c = 0; c < spanCols; c++) {
      const colIndex = anchorColIndex + c;
      if (colIndex >= maxCols) break; // clip to table right edge
      writes.push({
        rowIndex,
        colIndex,
        value: grid[r % gridRows][c % gridCols],
      });
    }
  }
  return writes;
}
